import React from 'react';
import { Link } from 'react-router-dom';
import { BlockMath, InlineMath } from 'react-katex';
import Citation from '../components/Citation/Citation';
import Footer from '../components/Footer/Footer';
import VisualizationContainer from '../components/Visualizations/VisualizationContainer';
import MultiParticleDemo from '../components/Visualizations/MultiParticleDemo';
import CollapsibleSection from '../components/CollapsibleSection/CollapsibleSection';
import { researchCitations, technicalSpecs } from '../data/research';
import './TechnologyPage.css';

function AcousticLevitation() {
  return (
    <>
      <header className="technology-hero">
        <div className="container">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>3D Acoustic Levitation</h1>
          <p className="technology-subtitle">
            Developing precise 3D particle control through acoustic hologram technology
          </p>
        </div>
      </header>

      <main className="technology-content">
        <div className="container">
          <section className="technology-section">
            <h2>Our Vision</h2>
            <p>
              We're developing acoustic hologram-based systems for precise 3D manipulation of particles, 
              gel beads, and living cells. Our goal is to create systems that use multiple interacting 
              pressure fields generated by phased array transducers, enabling simultaneous control of 
              multiple objects in arbitrary 3D configurations with independent positioning.
            </p>
            <p>
              The technology we're building will create precise acoustic pressure fields capable of 
              trapping and manipulating objects from nanometers to millimeters in size—all without 
              physical contact. This represents a fundamental shift toward programmable matter systems 
              where materials can be precisely organized in three-dimensional space.
            </p>
          </section>

          <section className="technology-section">
            <h2>The Science Behind It</h2>
            <p>
              Acoustic holograms are generated using phased array transducers that create complex 3D 
              pressure field patterns. When multiple acoustic waves interfere, they form pressure nodes 
              and antinodes—regions where particles can be trapped and manipulated. By dynamically 
              adjusting the phase and amplitude of individual transducers, these pressure fields can be 
              shaped and moved in real-time.
            </p>
            <p>
              Research by <Citation id={2} /> demonstrates that multiple acoustic holograms can create 
              precise 3D pressure fields for touchless manipulation. The study shows that this approach 
              enables manipulation of solid particles, gel beads, and living cells simultaneously, with 
              each object independently controlled in three-dimensional space.
            </p>
            <p>
              The acoustic fields are gentle, non-toxic, and maintain sterility—making them ideal for 
              biological applications where contactless manipulation is critical. Unlike optical methods, 
              acoustic manipulation works through optically opaque materials, opening possibilities for 
              in-material fabrication and deep-tissue applications.
            </p>

            <CollapsibleSection title="Physics & Mathematics">
              <h3>Standing Wave Formation</h3>
              <p>
                Acoustic levitation relies on standing waves created by the interference of acoustic waves. 
                When two waves of the same frequency travel in opposite directions, they form a standing 
                wave pattern with fixed nodes (zero displacement) and antinodes (maximum displacement).
              </p>
              
              <div className="equation">
                <div className="equation-label">Standing wave equation:</div>
                <BlockMath math="P(x, t) = A \sin(kx) \cos(\omega t)" />
              </div>
              
              <p>
                Where:
              </p>
              <ul>
                <li><span className="physics-term"><InlineMath math="P(x, t)" /></span> is the acoustic pressure at position <InlineMath math="x" /> and time <InlineMath math="t" /></li>
                <li><span className="physics-term"><InlineMath math="A" /></span> is the amplitude of the wave</li>
                <li><span className="physics-term"><InlineMath math="k = 2\pi/\lambda" /></span> is the wave number (spatial frequency), where <InlineMath math="\lambda" /> is the wavelength</li>
                <li><span className="physics-term"><InlineMath math="\omega = 2\pi f" /></span> is the angular frequency, where <InlineMath math="f" /> is the frequency in Hz</li>
                <li><span className="physics-term"><InlineMath math="x" /></span> is the spatial position</li>
                <li><span className="physics-term"><InlineMath math="t" /></span> is time</li>
              </ul>

              <h3>Pressure Nodes and Antinodes</h3>
              <p>
                Pressure nodes occur where sin(kx) = 0, creating regions of zero acoustic pressure. 
                Particles are trapped at these nodes due to acoustic radiation forces. Antinodes occur 
                where sin(kx) = ±1, creating regions of maximum pressure variation.
              </p>
              
              <div className="equation">
                <div className="equation-label">Node positions:</div>
                <BlockMath math="x_{\text{node}} = \frac{n\lambda}{2}, \quad \text{where } n = 0, 1, 2, 3, \ldots" />
              </div>

              <h3>3D Pressure Field Generation</h3>
              <p>
                Multiple phased array transducers create complex 3D pressure fields by combining waves 
                from different directions. The total pressure field is the superposition of waves from 
                orthogonal and diagonal transducer pairs.
              </p>
              
              <div className="equation">
                <div className="equation-label">3D pressure field:</div>
                <BlockMath math="P(x, y, z, t) = \sum_{i} A_i \sin(\mathbf{k}_i \cdot \mathbf{r}) \cos(\omega_i t + \phi_i)" />
              </div>
              
              <p>
                Where:
              </p>
              <ul>
                <li><span className="physics-term"><InlineMath math="\mathbf{r} = (x, y, z)" /></span> is the position vector</li>
                <li><span className="physics-term"><InlineMath math="\mathbf{k}_i" /></span> is the wave vector for transducer pair <InlineMath math="i" /></li>
                <li><span className="physics-term"><InlineMath math="\phi_i" /></span> is the phase offset, controlled to shape the field</li>
                <li>The summation includes waves from X, Y, Z, and diagonal transducer pairs</li>
              </ul>

              <h3>Acoustic Radiation Force</h3>
              <p>
                Particles are trapped and manipulated by acoustic radiation forces. For small particles 
                (much smaller than the wavelength), the primary radiation force is given by the Gor'kov 
                potential, which depends on the pressure and velocity fields.
              </p>
              
              <div className="equation">
                <div className="equation-label">Gor'kov potential (simplified):</div>
                <BlockMath math="U = V_p \left[ \frac{f_1 \langle p^2 \rangle}{2\rho c^2} - \frac{3}{4} f_2 \rho \langle v^2 \rangle \right]" />
              </div>
              
              <p>
                Where:
              </p>
              <ul>
                <li><span className="physics-term"><InlineMath math="U" /></span> is the potential energy</li>
                <li><span className="physics-term"><InlineMath math="V_p" /></span> is the particle volume</li>
                <li><span className="physics-term"><InlineMath math="f_1, f_2" /></span> are compressibility and density contrast factors</li>
                <li><span className="physics-term"><InlineMath math="p" /></span> is the acoustic pressure</li>
                <li><span className="physics-term"><InlineMath math="v" /></span> is the acoustic velocity</li>
                <li><span className="physics-term"><InlineMath math="\rho" /></span> is the fluid density</li>
                <li><span className="physics-term"><InlineMath math="c" /></span> is the speed of sound</li>
                <li>The angle brackets <InlineMath math="\langle \rangle" /> denote time-averaged quantities</li>
              </ul>

              <p>
                Particles move toward regions of minimum potential energy, which correspond to pressure 
                nodes in standing wave fields. The radiation force is derived from the gradient of this potential:
              </p>
              
              <div className="equation">
                <div className="equation-label">Radiation force:</div>
                <BlockMath math="\mathbf{F} = -\nabla U" />
              </div>

              <h3>Primary and Secondary Radiation Forces</h3>
              <p>
                Acoustic radiation forces can be categorized into primary and secondary forces. The primary 
                radiation force (Gor'kov force) acts on particles due to the acoustic field itself, while 
                secondary forces arise from acoustic streaming and other nonlinear effects.
              </p>
              
              <div className="equation">
                <div className="equation-label">Primary radiation force (full Gor'kov expression):</div>
                <BlockMath math="\mathbf{F}_{\text{primary}} = -\nabla \left[ V_p \left( \frac{f_1 \langle p^2 \rangle}{2\rho_0 c_0^2} - \frac{3\rho_0 f_2 \langle v^2 \rangle}{4} \right) \right]" />
              </div>
              
              <p>
                Where the contrast factors are:
              </p>
              <ul>
                <li><span className="physics-term"><InlineMath math="f_1 = 1 - \frac{\rho_0 c_0^2}{\rho_p c_p^2}" /></span> (compressibility contrast)</li>
                <li><span className="physics-term"><InlineMath math="f_2 = \frac{2(\rho_p - \rho_0)}{2\rho_p + \rho_0}" /></span> (density contrast)</li>
                <li>Subscript <InlineMath math="0" /> refers to the fluid medium, <InlineMath math="p" /> refers to the particle</li>
              </ul>

              <h3>Acoustic Streaming</h3>
              <p>
                Acoustic streaming is a steady fluid flow induced by acoustic waves. It occurs due to 
                nonlinear effects and viscous dissipation in the acoustic boundary layer. Streaming can 
                create secondary forces on particles and is particularly important in microfluidic applications.
              </p>
              
              <div className="equation">
                <div className="equation-label">Acoustic streaming velocity (simplified):</div>
                <BlockMath math="\mathbf{v}_s = \frac{1}{4\rho_0 c_0^2} \left( \frac{1}{\omega} \nabla \langle p^2 \rangle - \frac{2}{\omega^2} \nabla \cdot \langle \mathbf{v} \mathbf{v} \rangle \right)" />
              </div>
              
              <p>
                Streaming can enhance particle manipulation by creating additional flow patterns that 
                assist in positioning and transport. However, it can also cause unwanted particle motion 
                that must be accounted for in precise manipulation systems.
              </p>

              <h3>Particle Size Regimes</h3>
              <p>
                The behavior of particles in acoustic fields depends on their size relative to the acoustic 
                wavelength. Different physical models apply in different regimes:
              </p>
              
              <ul>
                <li><strong>Rayleigh regime</strong> (<InlineMath math="a \ll \lambda" />, where <InlineMath math="a" /> is particle radius): 
                  Gor'kov potential applies. Particles behave as point scatterers.</li>
                <li><strong>Mie regime</strong> (<InlineMath math="a \sim \lambda" />): 
                  Full scattering theory required. Multiple scattering modes become important.</li>
                <li><strong>Geometric regime</strong> (<InlineMath math="a \gg \lambda" />): 
                  Ray acoustics approximation. Particles experience forces from direct pressure gradients.</li>
              </ul>
              
              <p>
                For our frequency range of {technicalSpecs.frequencyRange}, wavelengths range from 
                approximately 1.7 mm (200 kHz in air) to 0.75 mm (2 MHz in water), enabling manipulation 
                of particles from {technicalSpecs.particleSizeRange} across multiple regimes.
              </p>

              <h3>Acoustic Impedance and Reflection</h3>
              <p>
                Acoustic impedance determines how acoustic waves interact with boundaries and particles. 
                The impedance mismatch between particle and medium affects the radiation force magnitude 
                and direction.
              </p>
              
              <div className="equation">
                <div className="equation-label">Acoustic impedance:</div>
                <BlockMath math="Z = \rho c = \frac{p}{v}" />
              </div>
              
              <p>
                Where <InlineMath math="Z" /> is the specific acoustic impedance. For effective levitation, 
                particles typically have different acoustic properties (density and/or compressibility) 
                than the surrounding medium, creating the contrast factors <InlineMath math="f_1" /> and <InlineMath math="f_2" /> 
                in the Gor'kov potential.
              </p>

              <h3>Force Magnitude and Scaling</h3>
              <p>
                The magnitude of the acoustic radiation force scales with several key parameters:
              </p>
              
              <div className="equation">
                <div className="equation-label">Force scaling:</div>
                <BlockMath math="F \propto V_p \frac{p_0^2}{\rho_0 c_0^2} \frac{a}{\lambda} f(\rho, \kappa)" />
              </div>
              
              <p>
                Where <InlineMath math="p_0" /> is the pressure amplitude and <InlineMath math="f(\rho, \kappa)" /> 
                is a function of density and compressibility contrasts. This shows that:
              </p>
              <ul>
                <li>Force increases with particle volume <InlineMath math="V_p" /></li>
                <li>Force scales with pressure squared <InlineMath math="p_0^2" /></li>
                <li>Force depends on the size-to-wavelength ratio <InlineMath math="a/\lambda" /></li>
                <li>Material properties determine the contrast factors</li>
              </ul>

              <h3>Frequency and Wavelength Relationship</h3>
              <p>
                The relationship between frequency, wavelength, and the speed of sound determines the 
                spatial resolution of the pressure field and the size of particles that can be manipulated.
              </p>
              
              <div className="equation">
                <div className="equation-label">Wave equation:</div>
                <BlockMath math="c = f\lambda = \frac{\omega}{k}" />
              </div>
              
              <p>
                Where <span className="physics-term"><InlineMath math="c" /></span> is the speed of sound (approximately 343 m/s in air, 
                1500 m/s in water). For our frequency range of {technicalSpecs.frequencyRange}, this gives 
                wavelengths from approximately 1.7 mm (at 200 kHz in air) down to 0.75 mm (at 2 MHz in water), 
                enabling manipulation of particles from {technicalSpecs.particleSizeRange}.
              </p>

              <h3>Acoustic Holography Principles</h3>
              <p>
                Acoustic holograms are created by precisely controlling the phase and amplitude of waves 
                from multiple transducers. The holographic pattern is computed to create a desired 3D 
                pressure field distribution, similar to optical holography but using sound waves.
              </p>
              
              <div className="equation">
                <div className="equation-label">Hologram reconstruction:</div>
                <BlockMath math="p(\mathbf{r}) = \sum_{n=1}^{N} A_n \frac{e^{ik|\mathbf{r} - \mathbf{r}_n|}}{|\mathbf{r} - \mathbf{r}_n|} e^{i\phi_n}" />
              </div>
              
              <p>
                Where <InlineMath math="N" /> transducers at positions <InlineMath math="\mathbf{r}_n" /> 
                emit waves with amplitudes <InlineMath math="A_n" /> and phases <InlineMath math="\phi_n" />. 
                The complex pressure field <InlineMath math="p(\mathbf{r})" /> is the superposition of 
                spherical waves from each transducer.
              </p>

              <h3>Beamforming and Phased Array Mathematics</h3>
              <p>
                Phased arrays use constructive and destructive interference to steer and shape acoustic beams. 
                The beam pattern is determined by the array geometry and phase distribution.
              </p>
              
              <div className="equation">
                <div className="equation-label">Array factor (beam pattern):</div>
                <BlockMath math="AF(\theta, \phi) = \sum_{n=1}^{N} A_n e^{i\mathbf{k} \cdot \mathbf{r}_n + i\phi_n}" />
              </div>
              
              <p>
                Where <InlineMath math="\mathbf{k}" /> is the wave vector in the direction <InlineMath math="(\theta, \phi)" />. 
                By adjusting phases <InlineMath math="\phi_n" />, we can steer the main beam and create 
                multiple simultaneous beams for independent particle control.
              </p>
              
              <div className="equation">
                <div className="equation-label">Beam steering phase:</div>
                <BlockMath math="\phi_n = -k(\mathbf{r}_n \cdot \hat{\mathbf{u}})" />
              </div>
              
              <p>
                Where <InlineMath math="\hat{\mathbf{u}}" /> is the unit vector in the desired beam direction. 
                This creates a phase gradient across the array that steers the beam.
              </p>

              <h3>Nonlinear Acoustic Effects</h3>
              <p>
                At high intensities, nonlinear effects become important. The acoustic wave equation becomes 
                nonlinear, leading to harmonic generation and wave distortion.
              </p>
              
              <div className="equation">
                <div className="equation-label">Nonlinear wave equation (simplified):</div>
                <BlockMath math="\frac{\partial^2 p}{\partial t^2} - c^2 \nabla^2 p = \frac{\beta}{\rho_0 c^2} \frac{\partial^2 p^2}{\partial t^2}" />
              </div>
              
              <p>
                Where <InlineMath math="\beta" /> is the nonlinearity parameter. This nonlinearity can 
                enhance radiation forces but also creates unwanted effects like streaming and heating that 
                must be managed.
              </p>

              <h3>Viscous and Boundary Layer Effects</h3>
              <p>
                Near boundaries and for small particles, viscous effects become important. The acoustic 
                boundary layer thickness determines when viscous damping affects particle motion.
              </p>
              
              <div className="equation">
                <div className="equation-label">Boundary layer thickness:</div>
                <BlockMath math="\delta = \sqrt{\frac{2\nu}{\omega}}" />
              </div>
              
              <p>
                Where <InlineMath math="\nu" /> is the kinematic viscosity. For particles smaller than 
                the boundary layer, viscous drag significantly affects motion and must be included in 
                force calculations.
              </p>
              
              <div className="equation">
                <div className="equation-label">Stokes drag force:</div>
                <BlockMath math="\mathbf{F}_{\text{drag}} = -6\pi \mu a \mathbf{v}" />
              </div>
              
              <p>
                Where <InlineMath math="\mu" /> is the dynamic viscosity and <InlineMath math="\mathbf{v}" /> 
                is the particle velocity relative to the fluid.
              </p>

              <h3>Time-Averaged vs Instantaneous Forces</h3>
              <p>
                Acoustic radiation forces are time-averaged effects. The instantaneous acoustic pressure 
                oscillates rapidly, but particles respond to the time-averaged force over many acoustic cycles.
              </p>
              
              <div className="equation">
                <div className="equation-label">Time-averaged force:</div>
                <BlockMath math="\langle \mathbf{F} \rangle = \frac{1}{T} \int_0^T \mathbf{F}(t) \, dt" />
              </div>
              
              <p>
                Where <InlineMath math="T = 2\pi/\omega" /> is the acoustic period. For stable trapping, 
                the time-averaged force must overcome thermal fluctuations and other disturbances.
              </p>

              <h3>Multi-Frequency Operation</h3>
              <p>
                Operating at multiple frequencies simultaneously enables more complex manipulation. Different 
                frequencies can create independent pressure fields that interact to form complex 3D patterns.
              </p>
              
              <div className="equation">
                <div className="equation-label">Multi-frequency pressure field:</div>
                <BlockMath math="P(\mathbf{r}, t) = \sum_{m=1}^{M} \sum_{n=1}^{N} A_{mn} \sin(\mathbf{k}_m \cdot \mathbf{r}) \cos(\omega_m t + \phi_{mn})" />
              </div>
              
              <p>
                Where <InlineMath math="M" /> frequencies <InlineMath math="\omega_m" /> are used, each 
                with <InlineMath math="N" /> transducer pairs. This enables independent control at different 
                scales and frequencies, optimizing manipulation for different particle sizes.
              </p>

              <h3>Phased Array Control</h3>
              <p>
                By controlling the phase <InlineMath math="\phi_i" /> and amplitude <InlineMath math="A_i" /> of each transducer, 
                we can dynamically shape and move pressure fields in real-time. This enables:
              </p>
              <ul>
                <li>Independent control of multiple particles simultaneously</li>
                <li>Real-time repositioning of trapped objects</li>
                <li>Creation of complex 3D patterns and structures</li>
                <li>Precise manipulation with {technicalSpecs.positioningPrecision} accuracy</li>
              </ul>

              <p>
                The phase control allows us to steer the pressure field by adjusting the relative timing 
                of waves from different transducers, while amplitude control adjusts the strength of 
                individual wave components. The combination enables full 3D control of the acoustic field, 
                creating pressure nodes at arbitrary positions for particle trapping and manipulation.
              </p>
            </CollapsibleSection>
          </section>

          <section className="technology-section">
            <h2>Target Capabilities</h2>
            <p>
              Based on current research and our development roadmap, we're working toward systems with 
              the following capabilities:
            </p>
            
            <VisualizationContainer 
              height="400px"
              caption="Interactive demonstration: Four particles moving independently along different 3D paths, demonstrating simultaneous multi-object manipulation. Rotate and zoom to explore."
            >
              <MultiParticleDemo />
            </VisualizationContainer>
            
            <ul className="capabilities-list">
              <li><strong>Multi-object manipulation:</strong> Simultaneous control of multiple particles with independent 3D positioning</li>
              <li><strong>Broad size range:</strong> Manipulation from {technicalSpecs.particleSizeRange}—spanning from nanoparticles to macroscopic objects</li>
              <li><strong>High precision:</strong> {technicalSpecs.positioningPrecision} positioning accuracy for precise spatial control</li>
              <li><strong>Biological compatibility:</strong> Non-contact, sterile operation that maintains cell viability</li>
              <li><strong>Material versatility:</strong> Works with particles, gel beads, living cells, and various biomaterials</li>
              <li><strong>Frequency range:</strong> {technicalSpecs.frequencyRange} for optimal manipulation across different scales</li>
            </ul>
          </section>

          <section className="technology-section">
            <h2>Research Foundation</h2>
            <p>
              Our development is grounded in peer-reviewed research that demonstrates the feasibility 
              and potential of acoustic hologram-based manipulation. The foundational work by Melde et al. 
              <Citation id={2} /> at the Max Planck Institute for Medical Research and Heidelberg University 
              demonstrates that touchless 3D printing using acoustic holograms is not only possible but offers 
              unique advantages over traditional methods.
            </p>
            <p>
              Key findings from this research include:
            </p>
            <ul className="capabilities-list">
              <li>Multiple acoustic holograms can create precise, independently controllable 3D pressure fields</li>
              <li>The technique enables manipulation of diverse materials—from solid particles to living cells</li>
              <li>Acoustic manipulation is non-contact, gentle, and non-toxic to cells</li>
              <li>Sterility is maintained throughout the process, critical for biological applications</li>
            </ul>
            <p>
              We're building on this research to develop commercial systems that translate these 
              laboratory demonstrations into practical programmable matter technologies.
            </p>
          </section>

          <section className="technology-section">
            <h2>Potential Applications</h2>
            <p>
              The applications we're exploring span multiple domains where precise, contactless 
              manipulation offers significant advantages:
            </p>
            <ul className="applications-list">
              <li><strong>Volumetric 3D printing:</strong> Layerless fabrication of complex structures throughout material volumes</li>
              <li><strong>Cell patterning and tissue engineering:</strong> Precise spatial organization of cells for organoid formation and regenerative medicine</li>
              <li><strong>Precision material assembly:</strong> Contactless assembly of micro- and nano-scale components</li>
              <li><strong>Drug delivery systems:</strong> Precise positioning and release of therapeutic particles</li>
              <li><strong>Biomedical research:</strong> Gentle manipulation of cells and biomaterials for experimental studies</li>
            </ul>
          </section>

          <section className="technology-section">
            <h2>Development Roadmap</h2>
            <p>
              As a startup in active research and development, we're working to translate the promising 
              research in acoustic holography into practical systems. Our development focuses on:
            </p>
            <ul className="capabilities-list">
              <li>Advancing phased array transducer technology for more complex pressure field generation</li>
              <li>Developing algorithms for real-time control of multiple independent objects</li>
              <li>Optimizing systems for specific applications in manufacturing and synthetic biology</li>
              <li>Scaling from laboratory demonstrations to commercial-grade systems</li>
            </ul>
            <p>
              We're actively seeking research collaborations and partnerships to accelerate development 
              and explore new applications for acoustic hologram-based manipulation.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}

export default AcousticLevitation;

